import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MessageList } from './message-list';
import { MessageInput } from './message-input';
import { Button } from '../ui/button';
import { ChevronDown, TimerReset, ShieldOff } from 'lucide-react';
import { db, rtdb } from '@/lib/firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, Timestamp, deleteDoc, getDoc } from '@firebase/firestore';
import { ref, set, remove, onValue, onDisconnect } from 'firebase/database';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { trackRuntimeMetric } from '@/lib/observability/runtime-metrics';
import { addNotification } from '@/services/notificationService';
import { EmailAPI } from '@/services/emailAPI';
import { useFriends } from '@/hooks/useFriends';
import type { FriendRecord } from '@/hooks/useFriends';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

interface ChatProps {
  receiverId?: string;
  groupId?: string;
  groupName?: string;
  groupParticipants?: string[];
  onEphemeralStateChange?: (enabled: boolean) => void;
}

interface ChatMessage {
  id: string;
  content: string;
  senderId: string;
  senderName: string;
  senderPhoto?: string | null;
  receiverId: string;
  groupId?: string;
  groupName?: string;
  participants: string[];
  seen: boolean;
  deliveredAt?: Date | null;
  deliveredTo?: Record<string, Date>;
  seenBy?: Record<string, Date>;
  readAt?: string | null;
  mediaUrl?: string;
  mediaType?: string;
  type: string;
  isEdited?: boolean;
  tempClientId?: string;
  optimisticStatus?: 'sending' | 'sent';
  createdAt: Date;
  replyTo?: {
    messageId: string;
    content: string;
    senderName: string;
  };
  ephemeral?: boolean;
  expiresAt?: Date | null;
}

const EPHEMERAL_INTERVAL_OPTIONS: Array<{ label: string; seconds: number }> = [
  { label: '10s', seconds: 10 },
  { label: '30s', seconds: 30 },
  { label: '1m', seconds: 60 },
  { label: '5m', seconds: 300 },
];

export function Chat({ receiverId, groupId, groupName, groupParticipants = [], onEphemeralStateChange }: ChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingMessages, setPendingMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [editContent, setEditContent] = useState('');
  const [forwardingMessage, setForwardingMessage] = useState<ChatMessage | null>(null);
  const [selectedForwardRecipients, setSelectedForwardRecipients] = useState<string[]>([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [ephemeralEnabled, setEphemeralEnabled] = useState(false);
  const [ephemeralIntervalSeconds, setEphemeralIntervalSeconds] = useState<number>(30);
  const [localEphemeralMessages, setLocalEphemeralMessages] = useState<ChatMessage[]>([]);
  const { user } = useAuth();
  const { friends } = useFriends();
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const pendingMessagesRef = useRef<ChatMessage[]>([]);
  const localEphemeralMessagesRef = useRef<ChatMessage[]>([]);
  const pendingInitialScrollRef = useRef(true);
  const typingPermissionDeniedRef = useRef(false);
  const typingWriteCooldownUntilRef = useRef(0);
  const typingWriteInFlightRef = useRef(false);
  const isGroupChat = Boolean(groupId);

  const parseTimestampValue = (value: unknown): Date | null => {
    if (!value) return null;

    if (value instanceof Timestamp) {
      return value.toDate();
    }

    if (value instanceof Date) {
      return Number.isFinite(value.getTime()) ? value : null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      return Number.isFinite(date.getTime()) ? date : null;
    }

    return null;
  };

  const parseTimestampMap = (value: unknown): Record<string, Date> => {
    if (!value || typeof value !== 'object') {
      return {};
    }

    return Object.entries(value as Record<string, unknown>).reduce<Record<string, Date>>((acc, [uid, raw]) => {
      const parsed = parseTimestampValue(raw);
      if (parsed) {
        acc[uid] = parsed;
      }
      return acc;
    }, {});
  };

  useEffect(() => {
    pendingMessagesRef.current = pendingMessages;
  }, [pendingMessages]);

  useEffect(() => {
    localEphemeralMessagesRef.current = localEphemeralMessages;
  }, [localEphemeralMessages]);

  useEffect(() => {
    onEphemeralStateChange?.(ephemeralEnabled);
  }, [ephemeralEnabled, onEphemeralStateChange]);

  useEffect(() => {
    if (!user) return;

    try {
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef,
        where('participants', 'array-contains', user.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const messagesData = snapshot.docs
            .map(doc => {
              const data = doc.data();
              const deliveredTo = parseTimestampMap(data.deliveredTo);
              const seenBy = parseTimestampMap(data.seenBy);
              const createdAt = data.createdAt instanceof Timestamp ? 
                data.createdAt.toDate() : 
                new Date(data.createdAt || Date.now());
              const deliveredAt = parseTimestampValue(data.deliveredAt);
              const expiresAt = parseTimestampValue(data.expiresAt);
              const explicitSeen = Boolean(data.seen);
              const seenFromMap = Boolean(user && seenBy[user.uid]);

              return {
                id: doc.id,
                content: data.content || '',
                senderId: data.senderId || '',
                senderName: data.senderName || '',
                senderPhoto: data.senderPhoto || '',
                receiverId: data.receiverId || '',
                groupId: data.groupId || undefined,
                groupName: data.groupName || undefined,
                participants: data.participants || [],
                seen: explicitSeen || seenFromMap,
                deliveredAt,
                deliveredTo,
                seenBy,
                readAt: data.readAt || null,
                mediaUrl: data.mediaUrl,
                mediaType: data.mediaType,
                type: data.type || 'text',
                isEdited: data.isEdited || false,
                tempClientId: typeof data.tempClientId === 'string' ? data.tempClientId : undefined,
                ephemeral: Boolean(data.ephemeral),
                expiresAt,
                createdAt,
              } as ChatMessage;
            })
            .filter((message) => {
              if (isGroupChat && groupId) {
                return message.groupId === groupId;
              }

              if (!receiverId) {
                return false;
              }

              return (
                (message.senderId === user.uid && message.receiverId === receiverId) ||
                (message.senderId === receiverId && message.receiverId === user.uid)
              );
            })
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

          const confirmedTempIds = new Set(
            messagesData.map((message) => message.tempClientId).filter(Boolean)
          );

          const unresolvedPending = pendingMessagesRef.current.filter((pending) => {
            if (!pending.tempClientId) return true;
            return !confirmedTempIds.has(pending.tempClientId);
          });

          const aliveEphemeral = localEphemeralMessagesRef.current.filter((message) => {
            if (!message.expiresAt) return true;
            return message.expiresAt.getTime() > Date.now();
          });

          if (aliveEphemeral.length !== localEphemeralMessagesRef.current.length) {
            setLocalEphemeralMessages(aliveEphemeral);
          }

          setPendingMessages(unresolvedPending);
          setMessages(
            [...messagesData, ...unresolvedPending, ...aliveEphemeral].sort(
              (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
            )
          );
          setLoading(false);
          scrollToBottom();

          // Mark received messages as seen and update unread count
          const unseenMessages = messagesData.filter(message => 
            message.senderId !== user.uid && !message.seenBy?.[user.uid]
          );
          
          if (unseenMessages.length > 0) {
            // Mark messages as seen with readAt timestamp
            const readTimestamp = new Date().toISOString();
            unseenMessages.forEach(async (message) => {
              try {
                const updatePayload: Record<string, unknown> = {
                  readAt: readTimestamp,
                  [`deliveredTo.${user.uid}`]: readTimestamp,
                  [`seenBy.${user.uid}`]: readTimestamp,
                };

                if (!message.deliveredAt) {
                  updatePayload.deliveredAt = serverTimestamp();
                }

                // Preserve legacy single-recipient field while future-proofing group logic.
                if ((message.participants?.length || 0) <= 2) {
                  updatePayload.seen = true;
                }

                await updateDoc(doc(db, 'messages', message.id), updatePayload);
              } catch (err) {
                console.error('Error marking message as seen:', err);
              }
            });
          }
        } catch (error) {
          console.error('Error processing messages:', error);
          setLoading(false);
        }
      }, (error) => {
        console.error('Error fetching messages:', error);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up message listener:', error);
      setLoading(false);
    }
  }, [groupId, isGroupChat, receiverId, user]);

  const conversationId = isGroupChat
    ? `group_${groupId || 'unknown'}`
    : user && receiverId
      ? [user.uid, receiverId].sort().join('_')
      : '';

  useEffect(() => {
    typingPermissionDeniedRef.current = false;
    typingWriteCooldownUntilRef.current = 0;
    typingWriteInFlightRef.current = false;
  }, [conversationId, user?.uid]);

  useEffect(() => {
    pendingInitialScrollRef.current = true;
    setShowScrollButton(false);
  }, [conversationId]);

  const setTypingStatus = async (isTyping: boolean): Promise<void> => {
    if (ephemeralEnabled) return;
    if (!user || !conversationId || typingPermissionDeniedRef.current) return;
    if (Date.now() < typingWriteCooldownUntilRef.current) return;
    if (typingWriteInFlightRef.current) return;

    const typingRef = ref(rtdb, `typing/${conversationId}/${user.uid}`);

    try {
      typingWriteInFlightRef.current = true;
      if (isTyping) {
        await set(typingRef, {
          typing: true,
          updatedAt: Date.now(),
        });
        void onDisconnect(typingRef)
          .remove()
          .catch((error) => {
            const code = String((error as { code?: string } | null)?.code || '').toLowerCase();
            const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
            const isPermissionDenied =
              code.includes('permission_denied') ||
              code.includes('permission-denied') ||
              message.includes('permission_denied') ||
              message.includes('permission denied');

            if (isPermissionDenied) {
              typingPermissionDeniedRef.current = true;
              typingWriteCooldownUntilRef.current = Date.now() + (5 * 60 * 1000);
              trackRuntimeMetric('chat.typing.permission_denied', 1, { phase: 'on_disconnect' });
              return;
            }

            console.error('Failed to register typing onDisconnect handler:', error);
          });
      } else {
        await remove(typingRef);
      }
    } catch (error) {
      const code = String((error as { code?: string } | null)?.code || '').toLowerCase();
      const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
      const isPermissionDenied =
        code.includes('permission_denied') ||
        code.includes('permission-denied') ||
        message.includes('permission_denied') ||
        message.includes('permission denied');

      if (isPermissionDenied) {
        typingPermissionDeniedRef.current = true;
        typingWriteCooldownUntilRef.current = Date.now() + (5 * 60 * 1000);
        trackRuntimeMetric('chat.typing.permission_denied', 1, { phase: 'set_or_remove' });
        return;
      }

      console.error('Failed to update typing status:', error);
    } finally {
      typingWriteInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!user || !conversationId) return;

    const typingRootRef = ref(rtdb, `typing/${conversationId}`);
    const unsubscribe = onValue(
      typingRootRef,
      (snapshot) => {
        const payload = (snapshot.val() || {}) as Record<string, { typing?: boolean; updatedAt?: number }>;
        const now = Date.now();

        if (isGroupChat) {
          const anyTyping = Object.entries(payload).some(([uid, typingState]) => {
            if (uid === user.uid) return false;
            const updatedAt = typeof typingState?.updatedAt === 'number' ? typingState.updatedAt : 0;
            const isFresh = now - updatedAt < 6000;
            return Boolean(typingState?.typing) && isFresh;
          });

          setIsOtherUserTyping(anyTyping);
          return;
        }

        const targetPayload = receiverId ? payload[receiverId] : null;
        const updatedAt = typeof targetPayload?.updatedAt === 'number' ? targetPayload.updatedAt : 0;
        const isFresh = now - updatedAt < 6000;
        setIsOtherUserTyping(Boolean(targetPayload?.typing) && isFresh);
      },
      (error) => {
        const code = String((error as { code?: string } | null)?.code || '').toLowerCase();
        const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
        const isPermissionDenied =
          code.includes('permission_denied') ||
          code.includes('permission-denied') ||
          message.includes('permission_denied') ||
          message.includes('permission denied');

        if (isPermissionDenied) {
          typingPermissionDeniedRef.current = true;
          setIsOtherUserTyping(false);
          trackRuntimeMetric('chat.typing.permission_denied', 1, { phase: 'subscribe' });
          return;
        }

        console.error('Failed to subscribe to typing status:', error);
      }
    );

    return () => {
      unsubscribe();
      setIsOtherUserTyping(false);
      void setTypingStatus(false);
    };
  }, [conversationId, isGroupChat, receiverId, user?.uid, ephemeralEnabled]);

  useEffect(() => {
    if (!localEphemeralMessages.length) return;

    const timer = window.setInterval(() => {
      const now = Date.now();

      setLocalEphemeralMessages((prev) => prev.filter((message) => {
        if (!message.expiresAt) return true;
        return message.expiresAt.getTime() > now;
      }));

      setMessages((prev) => prev.filter((message) => {
        if (!message.ephemeral || !message.expiresAt) return true;
        return message.expiresAt.getTime() > now;
      }));
    }, 350);

    return () => {
      window.clearInterval(timer);
    };
  }, [localEphemeralMessages.length]);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    if (!messages.length || !pendingInitialScrollRef.current) return;

    pendingInitialScrollRef.current = false;

    // Run after paint to ensure the DOM includes the full message list height.
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToBottom('auto');
      });
    });

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [messages.length, conversationId]);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom);
  };

  const handleSendMessage = async (content: string, mediaUrl?: string, mediaType?: string) => {
    if (!user || (!content.trim() && !mediaUrl)) return;
    if (!isGroupChat && !receiverId) return;

    if (ephemeralEnabled) {
      const now = Date.now();
      const expiresAt = new Date(now + (ephemeralIntervalSeconds * 1000));
      const localMessage: ChatMessage = {
        id: `local-ephemeral-${user.uid}-${now}-${Math.random().toString(36).slice(2, 7)}`,
        content: content.trim(),
        senderId: user.uid,
        senderName: user.displayName || 'Anonymous',
        senderPhoto: user.photoURL,
        receiverId: receiverId || '',
        groupId,
        groupName,
        participants: isGroupChat
          ? Array.from(new Set([...groupParticipants.filter(Boolean), user.uid]))
          : [user.uid, receiverId as string],
        seen: true,
        deliveredAt: new Date(),
        deliveredTo: { [user.uid]: new Date() },
        seenBy: { [user.uid]: new Date() },
        readAt: null,
        mediaUrl,
        mediaType,
        type: mediaType ? 'media' : 'text',
        createdAt: new Date(now),
        ephemeral: true,
        expiresAt,
        ...(replyingTo
          ? {
              replyTo: {
                messageId: replyingTo.id,
                content: replyingTo.content,
                senderName: replyingTo.senderName,
              },
            }
          : {}),
      };

      setLocalEphemeralMessages((prev) => [...prev, localMessage]);
      setMessages((prev) => [...prev, localMessage].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()));
      setReplyingTo(null);
      scrollToBottom('smooth');
      return;
    }

    const participants = isGroupChat
      ? Array.from(new Set(groupParticipants.filter(Boolean)))
      : [user.uid, receiverId as string];

    if (!participants.includes(user.uid)) {
      participants.push(user.uid);
    }

    const tempClientId = `${user.uid}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMessage: ChatMessage = {
      id: `temp-${tempClientId}`,
      tempClientId,
      content: content.trim(),
      senderId: user.uid,
      senderName: user.displayName || 'Anonymous',
      senderPhoto: user.photoURL,
      receiverId: receiverId || '',
      groupId,
      groupName,
      participants,
      seen: false,
      deliveredTo: {
        [user.uid]: new Date(),
      },
      seenBy: {
        [user.uid]: new Date(),
      },
      deliveredAt: null,
      readAt: null,
      mediaUrl,
      mediaType,
      type: mediaType ? 'media' : 'text',
      optimisticStatus: 'sending',
      createdAt: new Date(),
      ...(replyingTo
        ? {
            replyTo: {
              messageId: replyingTo.id,
              content: replyingTo.content,
              senderName: replyingTo.senderName,
            },
          }
        : {}),
    };

    setPendingMessages((prev) => [...prev, optimisticMessage]);
    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const messageData = {
        content: content.trim(),
        senderId: user.uid,
        senderName: user.displayName || 'Anonymous',
        senderPhoto: user.photoURL,
        receiverId: receiverId || null,
        groupId: groupId || null,
        groupName: groupName || null,
        participants,
        conversationType: isGroupChat ? 'group' : 'dm',
        createdAt: serverTimestamp(),
        seen: false,
        deliveredAt: null,
        deliveredTo: {
          [user.uid]: new Date().toISOString(),
        },
        seenBy: {
          [user.uid]: new Date().toISOString(),
        },
        tempClientId,
        ...(replyingTo && { 
          replyTo: {
            messageId: replyingTo.id,
            content: replyingTo.content,
            senderName: replyingTo.senderName
          }
        })
      };

      // Only add media fields if they exist
      if (mediaUrl && mediaType) {
        Object.assign(messageData, { mediaUrl, mediaType });
      }

      const docRef = await addDoc(collection(db, 'messages'), messageData);
      
      // Send in-app notifications to all recipients other than sender.
      const targetRecipients = participants.filter((participantId) => participantId !== user.uid);
      await Promise.allSettled(
        targetRecipients.map(async (targetUserId) => {
          try {
            await addNotification(targetUserId, 'new_message', {
              messageId: docRef.id,
              senderId: user.uid,
              senderName: user.displayName || 'Someone',
              senderPhoto: user.photoURL || undefined,
              messagePreview: content.trim().substring(0, 50),
              actionUrl: isGroupChat
                ? `/messages?group=${groupId}`
                : `/messages?user=${user.uid}`,
            });
          } catch (notifError) {
            console.error('Error sending notification:', notifError);
          }
        })
      );

      if (!isGroupChat && receiverId) {
        // Keep existing email notifications for direct messages.
        try {
          const receiverDoc = await getDoc(doc(db, 'users', receiverId));
          if (receiverDoc.exists()) {
            const receiverData = receiverDoc.data();
            const receiverEmail = receiverData.email;
            const receiverName = receiverData.displayName || 'there';

            if (receiverEmail) {
              const messagePreview = content.trim().substring(0, 150);

              await EmailAPI.sendNewMessageEmail(
                receiverEmail,
                receiverName,
                user.displayName || 'Someone',
                messagePreview || '📎 Sent a media file'
              );
            }
          }
        } catch (emailError) {
          console.error('Error sending email notification:', emailError);
        }
      }

      setPendingMessages((prev) =>
        prev.map((pending) =>
          pending.tempClientId === tempClientId
            ? { ...pending, optimisticStatus: 'sent' }
            : pending
        )
      );
      setMessages((prev) =>
        prev.map((pending) =>
          pending.tempClientId === tempClientId
            ? { ...pending, optimisticStatus: 'sent' }
            : pending
        )
      );
      
      setReplyingTo(null);
      scrollToBottom('smooth');
    } catch (error: any) {
      setPendingMessages((prev) => prev.filter((pending) => pending.tempClientId !== tempClientId));
      setMessages((prev) => prev.filter((message) => message.tempClientId !== tempClientId));
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleReply = (message: ChatMessage) => {
    setReplyingTo(message);
  };

  const handleDeleteMessage = async (messageId: string) => {
    const targetMessage = messages.find(m => m.id === messageId);
    if (!targetMessage || targetMessage.senderId !== user?.uid) {
      toast({
        title: 'Not allowed',
        description: 'You can only delete your own messages.',
        variant: 'destructive',
      });
      return;
    }

    if (targetMessage.ephemeral || messageId.startsWith('local-ephemeral-')) {
      setLocalEphemeralMessages((prev) => prev.filter((message) => message.id !== messageId));
      setMessages((prev) => prev.filter((message) => message.id !== messageId));
      toast({
        title: 'Success',
        description: 'Ephemeral message removed.',
      });
      return;
    }

    try {
      await deleteDoc(doc(db, 'messages', messageId));
      toast({
        title: 'Success',
        description: 'Message deleted successfully.',
      });
    } catch (error: any) {
      console.error('Error deleting message:', error);
      toast({
        title: 'Error', 
        description: 'Failed to delete message.',
        variant: 'destructive',
      });
    }
  };

  const handleEditRequest = (message: ChatMessage) => {
    if (message.senderId !== user?.uid) {
      toast({
        title: 'Not allowed',
        description: 'You can only edit your own messages.',
        variant: 'destructive',
      });
      return;
    }

    setEditingMessage(message);
    setEditContent(message.content || '');
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !user) return;

    const nextContent = editContent.trim();
    if (!nextContent) {
      toast({
        title: 'Empty message',
        description: 'Message content cannot be empty.',
        variant: 'destructive',
      });
      return;
    }

    if (editingMessage.senderId !== user.uid) {
      toast({
        title: 'Not allowed',
        description: 'You can only edit your own messages.',
        variant: 'destructive',
      });
      return;
    }

    if (editingMessage.ephemeral || editingMessage.id.startsWith('local-ephemeral-')) {
      setLocalEphemeralMessages((prev) => prev.map((message) => (
        message.id === editingMessage.id
          ? { ...message, content: nextContent, isEdited: true }
          : message
      )));
      setMessages((prev) => prev.map((message) => (
        message.id === editingMessage.id
          ? { ...message, content: nextContent, isEdited: true }
          : message
      )));
      setEditingMessage(null);
      setEditContent('');
      toast({
        title: 'Success',
        description: 'Ephemeral message updated.',
      });
      return;
    }

    try {
      await updateDoc(doc(db, 'messages', editingMessage.id), {
        content: nextContent,
        isEdited: true,
        editedAt: new Date().toISOString(),
      });
      setEditingMessage(null);
      setEditContent('');
      toast({
        title: 'Success',
        description: 'Message updated successfully.',
      });
    } catch (error) {
      console.error('Error editing message:', error);
      toast({
        title: 'Error',
        description: 'Failed to edit message.',
        variant: 'destructive',
      });
    }
  };

  const handleForwardRequest = (message: ChatMessage) => {
    setForwardingMessage(message);
    setSelectedForwardRecipients([]);
  };

  const cycleEphemeralInterval = () => {
    const currentIndex = EPHEMERAL_INTERVAL_OPTIONS.findIndex((option) => option.seconds === ephemeralIntervalSeconds);
    const nextIndex = currentIndex >= 0
      ? (currentIndex + 1) % EPHEMERAL_INTERVAL_OPTIONS.length
      : 0;
    setEphemeralIntervalSeconds(EPHEMERAL_INTERVAL_OPTIONS[nextIndex].seconds);
  };

  const ephemeralIntervalLabel = EPHEMERAL_INTERVAL_OPTIONS.find(
    (option) => option.seconds === ephemeralIntervalSeconds
  )?.label || `${ephemeralIntervalSeconds}s`;

  const toggleForwardRecipient = (friendId: string) => {
    setSelectedForwardRecipients(prev =>
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  const forwardCandidates = friends
    .map((friend: FriendRecord) => friend?.otherUser)
    .filter((otherUser) => otherUser?.id && (isGroupChat || otherUser.id !== receiverId));

  const smartReplies = useMemo(() => {
    const latestIncoming = [...messages]
      .reverse()
      .find((message) => message.senderId !== user?.uid && (message.content || '').trim() && message.type === 'text');

    let baseReplies: string[] = ['Sounds good', 'Thanks!', 'I will check and get back to you'];

    if (latestIncoming) {
      const content = (latestIncoming.content || '').toLowerCase();

      if (content.includes('thank') || content.includes('hvala')) {
        baseReplies = ['You are welcome!', 'Anytime', 'Happy to help'];
      } else if (content.includes('?')) {
        baseReplies = ['Yes, sounds good', 'Not sure yet, let me check', 'Can we discuss this later today?'];
      } else if (content.includes('call') || content.includes('poziv') || content.includes('video')) {
        baseReplies = ['Sure, starting soon', 'Give me 10 minutes', 'Can we schedule for later?'];
      } else if (content.includes('meet') || content.includes('location') || content.includes('where')) {
        baseReplies = ['I am on my way', 'I will share my location', 'Not nearby right now'];
      } else if (isGroupChat) {
        baseReplies = ['Sounds good everyone', 'Count me in', 'Thanks for the update'];
      } else {
        baseReplies = ['Got it', 'Thanks for the update', 'I will get back to you soon'];
      }
    }

    const adaptiveFromHistory = [...messages]
      .reverse()
      .filter((message) => message.senderId === user?.uid && message.type === 'text')
      .map((message) => (message.content || '').trim())
      .filter((text) => text.length >= 4 && text.length <= 64)
      .filter((text) => !/[.!?]{2,}/.test(text))
      .slice(0, 10)
      .filter((text, index, array) => array.findIndex((value) => value.toLowerCase() === text.toLowerCase()) === index)
      .slice(0, 3);

    return [...baseReplies, ...adaptiveFromHistory]
      .filter((text, index, array) => array.findIndex((value) => value.toLowerCase() === text.toLowerCase()) === index)
      .slice(0, 6);
  }, [isGroupChat, messages, user?.uid]);

  const outgoingMessagesCount = useMemo(
    () => messages.filter((message) => message.senderId === user?.uid && message.type === 'text').length,
    [messages, user?.uid]
  );

  const handleConfirmForward = async () => {
    if (!user || !forwardingMessage || selectedForwardRecipients.length === 0) return;

    if (forwardingMessage.ephemeral || forwardingMessage.id.startsWith('local-ephemeral-')) {
      toast({
        title: 'Not available',
        description: 'Ephemeral local-only messages cannot be forwarded.',
        variant: 'destructive',
      });
      return;
    }

    setIsForwarding(true);

    try {
      const sendResults = await Promise.allSettled(
        selectedForwardRecipients.map(async (targetUserId) => {
          const messageData: Record<string, unknown> = {
            content: forwardingMessage.content || '',
            senderId: user.uid,
            senderName: user.displayName || 'Anonymous',
            senderPhoto: user.photoURL,
            receiverId: targetUserId,
            participants: [user.uid, targetUserId],
            createdAt: serverTimestamp(),
            seen: false,
            type: forwardingMessage.type || 'text',
            forwarded: true,
            forwardedFromMessageId: forwardingMessage.id,
          };

          if (forwardingMessage.mediaUrl && forwardingMessage.mediaType) {
            messageData.mediaUrl = forwardingMessage.mediaUrl;
            messageData.mediaType = forwardingMessage.mediaType;
          }

          const forwardedDoc = await addDoc(collection(db, 'messages'), messageData);

          await addNotification(targetUserId, 'new_message', {
            messageId: forwardedDoc.id,
            senderId: user.uid,
            senderName: user.displayName || 'Someone',
            senderPhoto: user.photoURL || undefined,
            messagePreview: (forwardingMessage.content || 'Forwarded a message').substring(0, 50),
            actionUrl: `/messages?user=${user.uid}`,
          });
        })
      );

      const successCount = sendResults.filter(result => result.status === 'fulfilled').length;
      const failedCount = sendResults.length - successCount;

      setForwardingMessage(null);
      setSelectedForwardRecipients([]);

      toast({
        title: successCount > 0 ? 'Message forwarded' : 'Forward failed',
        description: successCount > 0
          ? failedCount > 0
            ? `Forwarded to ${successCount} friend(s), failed for ${failedCount}.`
            : `Forwarded to ${successCount} friend(s).`
          : 'Could not forward message.',
        variant: successCount > 0 ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Error forwarding message:', error);
      toast({
        title: 'Error',
        description: 'Failed to forward message.',
        variant: 'destructive',
      });
    } finally {
      setIsForwarding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-white to-gray-50/50 overflow-hidden">
      {/* Messages Area */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-4"
        onScroll={handleScroll}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f8fafc' fill-opacity='0.6'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <MessageList 
          messages={messages} 
          onReply={handleReply}
          onDelete={handleDeleteMessage}
          onEdit={handleEditRequest}
          onForward={handleForwardRequest}
        />
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Button
          variant="outline"
          size="icon"
          className="absolute bottom-24 right-6 rounded-full shadow-lg bg-white hover:bg-gray-50 border-gray-200"
          onClick={() => scrollToBottom('smooth')}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      )}

      {/* Message Input Area */}
      <div className="border-t border-gray-200/50 bg-white/80 backdrop-blur-sm flex-shrink-0">
        {isOtherUserTyping ? (
          <div className="px-4 pt-2 pb-1 text-xs text-blue-600 flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse"></span>
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse [animation-delay:120ms]"></span>
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse [animation-delay:240ms]"></span>
            </span>
            <span>Typing...</span>
          </div>
        ) : null}

        <div className="px-4 pt-2 pb-1 border-b border-gray-100/80 bg-gradient-to-r from-amber-50/70 to-orange-50/40">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={ephemeralEnabled} onCheckedChange={setEphemeralEnabled} />
              <span className="text-xs font-medium text-gray-700 inline-flex items-center gap-1.5">
                <ShieldOff className="w-3.5 h-3.5 text-amber-600" />
                Private Ephemeral (local-only, no DB storage)
              </span>
            </div>

            {ephemeralEnabled ? (
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-500 mr-1 inline-flex items-center gap-1">
                  <TimerReset className="w-3 h-3" />
                  Auto-delete:
                </span>
                {EPHEMERAL_INTERVAL_OPTIONS.map((option) => (
                  <Button
                    key={option.seconds}
                    type="button"
                    size="sm"
                    variant={ephemeralIntervalSeconds === option.seconds ? 'default' : 'outline'}
                    className="h-6 px-2 text-[11px]"
                    onClick={() => setEphemeralIntervalSeconds(option.seconds)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <MessageInput 
          onSendMessage={handleSendMessage} 
          replyingTo={replyingTo}
          suggestedReplies={smartReplies}
          conversationKey={conversationId || (isGroupChat ? `group-${groupId || 'unknown'}` : `dm-${receiverId || 'unknown'}`)}
          outgoingMessagesCount={outgoingMessagesCount}
          localOnlyMode={ephemeralEnabled}
          ephemeralIntervalLabel={ephemeralIntervalLabel}
          onToggleLocalOnlyMode={() => setEphemeralEnabled((prev) => !prev)}
          onCycleEphemeralInterval={cycleEphemeralInterval}
          onCancelReply={() => setReplyingTo(null)}
          onTypingChange={(typing) => {
            if (ephemeralEnabled) return;
            void setTypingStatus(typing);
          }}
        />
      </div>

      <Dialog open={!!editingMessage} onOpenChange={(open) => !open && setEditingMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit message</DialogTitle>
            <DialogDescription>Update your message text and save changes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Edit message"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingMessage(null)}>Cancel</Button>
              <Button onClick={handleSaveEdit}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!forwardingMessage} onOpenChange={(open) => !open && setForwardingMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Forward message</DialogTitle>
            <DialogDescription>Select one or more friends to forward this message.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {forwardCandidates.length === 0 ? (
              <p className="text-sm text-gray-500">No friends available to forward to.</p>
            ) : (
              forwardCandidates.map((friend) => (
                <label key={friend.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer">
                  <Checkbox
                    checked={selectedForwardRecipients.includes(friend.id)}
                    onCheckedChange={() => toggleForwardRecipient(friend.id)}
                  />
                  <img
                    src={friend.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${friend.name}`}
                    alt={friend.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{friend.name}</p>
                    {friend.username && <p className="text-xs text-gray-500 truncate">@{friend.username}</p>}
                  </div>
                </label>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setForwardingMessage(null)}>Cancel</Button>
            <Button
              onClick={handleConfirmForward}
              disabled={selectedForwardRecipients.length === 0 || isForwarding}
            >
              {isForwarding ? 'Forwarding...' : 'Forward'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}